import React, { useState, useEffect, useCallback } from "react";
import { useNavigate } from "react-router-dom";

// ページ要素
import Navbar from "./components/navbar/Navbar";
import Dashboard from "./components/form/Dashboard";
import Controllers from "./components/controllers/Controllers";
import NotificationSnippet from "./components/notifications/NotificationSnippet";
import Grid from "./components/grid/ProcGrid";
// import Update from './components/UpdateSchedule';
import SyncButton from "./components/ui/SyncButton";
import { useLastClicked } from "./hooks/useLastClicked";
import Footer from "./components/footer/Footer";
// AgGrid
import { GridApi } from "ag-grid-enterprise";
// Hooks
import useFetchData from "./hooks/useFetchData";
import useHolidays from "./hooks/useHolidays";
// CSS
import "./App.css";
import Spinner from "./components/ui/Spinner";
// Redux
import { useSelector, useDispatch } from "react-redux";
// store.tsのRootStateをインポート
import { AppDispatch, RootState } from "./redux/store";
// 実績情報
import { setShouldFetch } from "./redux/shouldFetchSlice";
// 実績情報取得クエリパラメータ
import { queryParamsSlice } from "./redux/queryParamsSlice";
import { setIsChangeDashboardPlanProcMach } from "./redux/controllerStateSlice";
import { fetchMachineStatus, setMachStatus } from "./redux/recMachStartedSlice";
// 通知一覧取得
// import { fetchNotifications } from "./redux/notificationsSlice";

import { fetchProcListLayout } from "./redux/procListLayoutSlice";
import { Helmet } from "react-helmet";

import { getUserName } from "./utils/utils";
import apiAxios from "./utils/apiAxios";
import { PfmUser } from "./models/PfmUser";

const App: React.FC = () => {
  const navigate = useNavigate();
  const dispatch: AppDispatch = useDispatch();
  const [gridApi, setGridApi] = useState<GridApi | null>(null);
  const [columnApi, setColumnApi] = useState<any | null>(null);
  const onGridReady = (params: { api: GridApi; columnApi: any }) => {
    setGridApi(params.api);
    setColumnApi(params.columnApi);
  };
  // sticky制御
  const isControllerSticky = useSelector(
    (state: RootState) => state.controllerState.isControllerSticky
  );
  // コントローラー制御
  const isChangeDashboardPlanProcMach = useSelector(
    (state: RootState) => state.controllerState.isChangeDashboardPlanProcMach
  );
  // 工場・加工・機械情報
  const plant_proc_mach = useSelector(
    (state: RootState) => state.procs.plantProcMach
  );
  // 実績情報取得用クエリパラメータ
  const queryParams = useSelector(
    (state: RootState) => state.queryParams.value
  );
  // データ取得日時
  const { timeSinceLastClick, setLastClicked } = useLastClicked();
  const holidays = useHolidays();
  const { rowData, isLoading } = useFetchData(
    `/proc_list`,
    queryParams,
    holidays
  );
  const selectedPlantCode = useSelector(
    (state: RootState) => state.queryParams.selectedPlantCode
  );
  const selectedProcCode = useSelector(
    (state: RootState) => state.queryParams.selectedProcCode
  );
  const selectedMachCodes = useSelector(
    (state: RootState) => state.queryParams.machCode
  );

  // 機械
  const machStatus = useSelector(
    (state: RootState) => state.recMachStarted.machStatus
  );

  // 実績情報取得ボタン
  const handleButtonClick = useCallback(
    (plant_code: string, proc_code: string, mach_codes: string[]) => {
      // ブックマークへの保存を容易にするため、パスパラメータを指定してリロードを行う
      //　クエリパスが変わらない場合は画面遷移せずにそのまま検索する
      navigate(
        `/?plant_code=${plant_code}&proc_code=${proc_code}&mach_code=${mach_codes.join()}`
      );
      localStorage.setItem("plant_code", plant_code);
      localStorage.setItem("proc_code", proc_code);
      localStorage.setItem("mach_code", mach_codes.join());

      // 作業実績取得
      dispatch(setShouldFetch(true));

      // 作業実績の更新日時設定
      setLastClicked(new Date());

      // グリッド列定義更新
      dispatch(
        fetchProcListLayout(
          plant_code,
          proc_code,
          mach_codes.length > 0 ? mach_codes : [""]
        )
      );

      // 機械の状態更新
      if (mach_codes.length === 1) {
        dispatch(fetchMachineStatus(plant_code, proc_code, mach_codes[0]));
      } else {
        dispatch(setMachStatus(null));
      }

      // 通知を取得
      // dispatch(fetchNotifications(plant_code, proc_code, mach_codes));

      // コントローラーの状態更新
      dispatch(setIsChangeDashboardPlanProcMach(false));
    },
    [setLastClicked, dispatch, navigate]
  );

  useEffect(() => {
    if (isChangeDashboardPlanProcMach) {
      // 機械の状態を更新
      dispatch(setMachStatus(null));
    }
  }, [isChangeDashboardPlanProcMach, dispatch]);

  useEffect(() => {
    (async () => {
      let plant_code = localStorage.getItem("plant_code") ?? "";
      let proc_code = localStorage.getItem("proc_code") ?? "";
      let mach_codes = localStorage.getItem("mach_code") ?? "";

      if (plant_code === "" && proc_code === "" && mach_codes === "") {
        // クエリパラメータで工場・加工・機械が指定されていない場合、ユーザー情報から取得
        const tokenUserName = (await getUserName().then()).toString();
        const users = await apiAxios.get<PfmUser>(
          "/pfm_users/" + tokenUserName
        );
        if (users.data.user_id) {
          plant_code = users.data.plant_code;
          proc_code = users.data.proc_code;
          mach_codes = users.data.mach_code;
        }
      }

      let doSearch = false;
      // パスパラメータから取得した工場コードを工場選択へセット
      if (plant_code !== "") {
        doSearch = true;
        dispatch(
          queryParamsSlice.actions.setSelectedPlantCode(
            plant_code == null ? "" : plant_code
          )
        );
      }
      // パスパラメータから取得した加工コードを加工選択へセット
      if (proc_code !== "") {
        doSearch = true;
        dispatch(
          queryParamsSlice.actions.setSelectedProcCode(
            proc_code == null ? "" : proc_code
          )
        );
      }
      // パスパラメータから取得した機械コードを機械選択へセット
      if (mach_codes !== "") {
        doSearch = true;
        dispatch(queryParamsSlice.actions.setMachCode(mach_codes.split(",")));
      }

      if (doSearch) {
        handleButtonClick(
          plant_code ?? "",
          proc_code ?? "",
          mach_codes?.split(",") ?? []
        );
      }
    })();
  }, [dispatch]);

  return (
    <>
      <Helmet>
        {/*<script src="https://cdn.jsdelivr.net/npm/@undecaf/zbar-wasm@0.10.1/dist/index.js"></script> */}
        <script src="zbar/index.js"></script>
        {machStatus === null && <title>Process Flow Manager</title>}
        {/* 選択した機械名をタイトルに表示 */}
        <title>
          {machStatus === null
            ? "Process Flow Manager"
            : machStatus?.status_code === 1 && machStatus !== null
            ? "◎" + machStatus?.mach_name
            : machStatus?.mach_name}
        </title>
      </Helmet>
      <Navbar />
      {/* トップ画面 */}
      <div className="main-container">
        {/* データ読み込みボタン */}
        <div className="sync-item">
          <SyncButton
            onClick={() =>
              handleButtonClick(
                selectedPlantCode,
                selectedProcCode,
                selectedMachCodes
              )
            }
            timeSinceLastClick={timeSinceLastClick}
          />
        </div>
        {/* 工場、加工、機械・・・選択 */}
        <div className="dashboard-item">
          {plant_proc_mach.length > 0 ? <Dashboard /> : null}
        </div>
        {/* 実績登録、機械、作業打刻・・・選択 */}
        <div
          className={
            isControllerSticky
              ? "controllers-item controllers-sticky"
              : "controllers-item"
          }
        >
          <Controllers
            rowData={rowData}
            gridApi={gridApi}
            columnApi={columnApi}
          />
        </div>
        {/* 通知 */}
        <div className="notification-item notification-sticky">
          <NotificationSnippet />
        </div>
        {/* 実績一覧 */}
        <div className="grid-item">
          {isLoading && <Spinner />}
          <Grid rowData={rowData} onGridReady={onGridReady} />
        </div>
      </div>
      {/* <Update gridApi={gridApi} /> */}
      <Footer />
    </>
  );
};

export default App;